import React from "react";
import classnames from "classnames";

import LocationPinOutlined from "@vahak/core/dist/standard-icons/location-pin-outlined.svg";
import LocationArrow from "@vahak/core/dist/standard-icons/location-arrow.svg";
import { LocationSearchSource } from "@vahak/core/dist/_services/city-search.service";

import { LoadQuoteValues } from "../../load-quote";
import QuoteFormLocationField, {
    QuoteFormLocationFieldHandle,
    QuoteFormLocationMessages
} from "./fields/QuoteFormLocationField";
import FieldStyles from "./QuoteFormFields.module.scss";
import { LoadQuoteFormVariant, ROUTE_LOAD_QUOTE_FIELD_IDS, HERO_LOAD_QUOTE_FIELD_IDS } from "./loadQuoteForm.constants";
import RouteStyles from "../../compact-load-posting/steps/RouteDetailsStep.module.scss";

// Stable element identities so the memoized react-select `components` in
// QuoteFormLocationField never change across parent re-renders (prevents focus loss).
const PICKUP_ICON = <LocationPinOutlined className={FieldStyles.fieldIconPin} aria-hidden />;
const DROP_ICON = <LocationArrow className={FieldStyles.fieldIcon} aria-hidden />;

export type LoadQuoteFormFieldLabels = {
    pickupPlaceholder: string;
    dropPlaceholder: string;
    pickupLabel?: string;
    dropLabel?: string;
};

type LoadQuoteFormFieldsProps = {
    variant: LoadQuoteFormVariant;
    values: LoadQuoteValues;
    labels: LoadQuoteFormFieldLabels;
    locationMessages?: QuoteFormLocationMessages;
    onChange: (patch: Partial<LoadQuoteValues>) => void;
    onPickupInputChange?: (value: string) => void;
    onDropInputChange?: (value: string) => void;
};

const LoadQuoteFormFields = ({
    variant,
    values,
    labels,
    locationMessages,
    onChange,
    onPickupInputChange,
    onDropInputChange
}: LoadQuoteFormFieldsProps) => {
    const fieldIds = variant === "routeStep" ? ROUTE_LOAD_QUOTE_FIELD_IDS : HERO_LOAD_QUOTE_FIELD_IDS;
    const isRouteStep = variant === "routeStep";
    const dropFieldRef = React.useRef<QuoteFormLocationFieldHandle>(null);

    const pickupField = (
        <QuoteFormLocationField
            id={fieldIds["quote-pickup-city"]}
            name="pickupCity"
            placeholder={labels.pickupPlaceholder}
            icon={PICKUP_ICON}
            value={values.source}
            onChange={(source) => {
                onChange({ source });
                if (source && !isRouteStep) {
                    requestAnimationFrame(() => dropFieldRef.current?.focus());
                }
            }}
            onInputValueChange={onPickupInputChange}
            locationSearchSource={LocationSearchSource.HomepageV2LoadSourcePosting}
            inSheet={isRouteStep}
            fieldRole="pickup"
            messages={locationMessages}
        />
    );

    const dropField = (
        <QuoteFormLocationField
            id={fieldIds["quote-drop-city"]}
            name="dropCity"
            placeholder={labels.dropPlaceholder}
            icon={DROP_ICON}
            value={values.destination}
            onChange={(destination) => onChange({ destination })}
            selectRef={dropFieldRef}
            onInputValueChange={onDropInputChange}
            locationSearchSource={LocationSearchSource.HomepageV2LoadDestinationPosting}
            inSheet={isRouteStep}
            fieldRole="drop"
            messages={locationMessages}
        />
    );

    if (isRouteStep) {
        return (
            <div className={FieldStyles.root}>
                <div className={RouteStyles.formFields}>
                    <div className={classnames(RouteStyles.fieldGroup, RouteStyles.fieldGroupPickup)}>
                        {labels.pickupLabel ? (
                            <label className={RouteStyles.label} htmlFor={`${fieldIds["quote-pickup-city"]}__input`}>
                                {labels.pickupLabel}
                            </label>
                        ) : null}
                        {pickupField}
                    </div>

                    <div className={classnames(RouteStyles.fieldGroup, RouteStyles.fieldGroupDrop)}>
                        {labels.dropLabel ? (
                            <label className={RouteStyles.label} htmlFor={`${fieldIds["quote-drop-city"]}__input`}>
                                {labels.dropLabel}
                            </label>
                        ) : null}
                        {dropField}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={FieldStyles.root}>
            <div className={FieldStyles.heroRoute}>
                <div className={FieldStyles.heroRail} aria-hidden>
                    <span className={FieldStyles.heroRailDot} />
                    <span className={FieldStyles.heroRailLine} />
                    <span className={FieldStyles.heroRailSquare} />
                </div>
                <div className={FieldStyles.heroFields}>
                    <div className={FieldStyles.heroField}>
                        {labels.pickupLabel ? (
                            <label
                                className={FieldStyles.heroLabel}
                                htmlFor={`${fieldIds["quote-pickup-city"]}__input`}
                            >
                                {labels.pickupLabel}
                            </label>
                        ) : null}
                        {pickupField}
                    </div>
                    <div className={FieldStyles.heroField}>
                        {labels.dropLabel ? (
                            <label className={FieldStyles.heroLabel} htmlFor={`${fieldIds["quote-drop-city"]}__input`}>
                                {labels.dropLabel}
                            </label>
                        ) : null}
                        {dropField}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LoadQuoteFormFields;
